import { IntegrationEventType } from '@prisma/client';
import { IntegrationEventItem, IntegrationReportData } from '@zond/api-interfaces';
import { CheckCircle2, Circle, ListTodo } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CollapsibleEventSection } from './shared/collapsible-event-section';
import { EventItem } from './shared/event-item';
import { getLatestEventPerEntity, shouldUseCollapsibleView } from './shared/event-utils';
import { IntegrationCardHeader } from './shared/integration-card-header';

interface ClickUpIntegrationCardProps {
  data: IntegrationReportData;
}

const getEventIcon = (eventType: IntegrationEventType) => {
  switch (eventType) {
    case IntegrationEventType.TaskCompleted:
      return <CheckCircle2 className="h-4 w-4" />;
    case IntegrationEventType.TaskAssigned:
      return <Circle className="h-4 w-4" />;
    default:
      return <ListTodo className="h-4 w-4" />;
  }
};

const getEventColor = (eventType: IntegrationEventType) => {
  switch (eventType) {
    case IntegrationEventType.TaskCompleted:
      return 'text-green-500';
    case IntegrationEventType.TaskAssigned:
      return 'text-purple-500';
    default:
      return 'text-gray-500';
  }
};

export const ClickUpIntegrationCard: React.FC<ClickUpIntegrationCardProps> = ({ data }) => {
  const { t } = useTranslation('reports');
  const { events, summary } = data;
  const useCollapsible = shouldUseCollapsibleView(events);
  const displayEvents = useCollapsible ? events : getLatestEventPerEntity(events);

  const completedTasks = displayEvents.filter((e) => e.eventType === IntegrationEventType.TaskCompleted);
  const assignedTasks = displayEvents.filter((e) => e.eventType === IntegrationEventType.TaskAssigned);
  const otherTasks = displayEvents.filter(
    (e) =>
      e.eventType !== IntegrationEventType.TaskCompleted && e.eventType !== IntegrationEventType.TaskAssigned
  );

  const renderEvent = (event: IntegrationEventItem) => (
    <EventItem
      key={event.id}
      event={event}
      icon={getEventIcon(event.eventType)}
      colorClass={getEventColor(event.eventType)}
    />
  );

  return (
    <div className="p-4 bg-muted/50 rounded-lg border">
      <IntegrationCardHeader
        integrationName="ClickUp"
        logoUrl="/integration-logos/clickup.svg"
        totalEvents={displayEvents.length}
      />
      {useCollapsible ? (
        <div className="space-y-4">
          <CollapsibleEventSection
            title={t('integration.sections.completedTasks')}
            icon={<CheckCircle2 className="h-4 w-4" />}
            events={completedTasks}
            renderEvent={renderEvent}
          />
          <CollapsibleEventSection
            title={t('integration.sections.assignedTasks')}
            icon={<Circle className="h-4 w-4" />}
            events={assignedTasks}
            renderEvent={renderEvent}
          />
          <CollapsibleEventSection
            title={t('integration.sections.otherTasks')}
            icon={<ListTodo className="h-4 w-4" />}
            events={otherTasks}
            renderEvent={renderEvent}
          />
        </div>
      ) : (
        <div className="space-y-1 max-h-[200px] overflow-y-auto custom-scrollbar">
          {displayEvents.map(renderEvent)}
        </div>
      )}
    </div>
  );
};
